import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getDemoDataForCollection } from '../demoData';
import { 
  ShieldCheck, Trash2, CheckCircle, Search, Filter, 
  ChevronUp, ChevronDown, User, ExternalLink, Eye, X, 
  BookOpen, Briefcase, Award, Phone, Mail, MapPin, 
  DollarSign, FileText, Building, Check, Clock,
  Pin, Plus, GraduationCap, Github, Linkedin, Globe
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TrainerDetailDrawer = ({ trainer, onClose, onUpdateStatus }) => {
  if (!trainer) return null;

  const displayName = trainer.name || `${trainer.firstName || ''} ${trainer.lastName || ''}`.trim() || 'Trainer';
  const specialization = trainer.primarySpecialization || trainer.specialization || 'Technical Faculty';

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[680px] lg:w-[780px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300 text-white">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 bg-zinc-900/60 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-[#8B1A2D]/20 text-[#BE123C] border border-[#8B1A2D]/40 text-[10px] font-black uppercase tracking-widest rounded-full">
                Portal 02 &bull; Faculty Dossier
              </span>
              <span className="text-xs font-mono font-bold text-zinc-400">
                {trainer.trainerRef || trainer.id}
              </span>
            </div>
            <h2 className="text-2xl font-black text-white">{displayName}</h2>
            <p className="text-xs text-zinc-400 font-bold mt-1 flex items-center gap-2">
              <Award size={14} className="text-amber-400" /> {specialization} &bull; {trainer.currentEmployer || 'Faculty Mentor'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Status</span>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                {trainer.status || 'Active'}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Nexus Role</span>
              <span className="text-xs font-bold text-amber-400 uppercase tracking-wider">
                {trainer.nexusAccess || 'Faculty Access'}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Experience</span>
              <span className="text-xs font-bold text-white font-mono">{trainer.yearsOfExperience || 5} Years</span>
            </div>
          </div>

          {/* Section 1: Contact & Personal Identity */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] border-b border-zinc-800 pb-2 flex items-center gap-2">
              <User size={14} /> Personal &amp; Contact Credentials
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Email:</span>
                <span className="text-zinc-200 font-mono">{trainer.email || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Phone / M-Pesa:</span>
                <span className="text-zinc-200 font-mono">{trainer.phone || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">National ID / Passport:</span>
                <span className="text-zinc-200 font-mono">{trainer.nationalId || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Location (County / Country):</span>
                <span className="text-zinc-200">{trainer.county || '-'}, {trainer.country || 'Kenya'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Gender &amp; DOB:</span>
                <span className="text-zinc-200">{trainer.gender || '-'} &bull; {trainer.dateOfBirth || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Postal Address:</span>
                <span className="text-zinc-200">{trainer.postalAddress || '-'}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Academic & Industry Experience */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] border-b border-zinc-800 pb-2 flex items-center gap-2">
              <GraduationCap size={14} /> Academic Credentials &amp; Industry Track Record
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Highest Degree Attained:</span>
                <span className="text-zinc-200 font-bold">{trainer.highestEducationLevel || "Bachelor's Degree"}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Graduating University:</span>
                <span className="text-zinc-200">{trainer.universityGraduated || '-'} ({trainer.graduationYear || '-'})</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Current Full-Time Employer:</span>
                <span className="text-zinc-200 font-bold">{trainer.currentEmployer || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Current Job Title:</span>
                <span className="text-zinc-200">{trainer.currentJobTitle || '-'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-zinc-500 font-bold block mb-1">Secondary Technical Stacks:</span>
                <span className="text-zinc-300 font-mono text-[11px]">{trainer.secondaryCompetencies || '-'}</span>
              </div>
            </div>

            {/* Online Links */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
              {trainer.githubUrl && (
                <a href={trainer.githubUrl} target="_blank" rel="noreferrer" className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 text-xs text-white rounded-lg flex items-center gap-1 font-bold">
                  <Github size={11} /> GitHub
                </a>
              )}
              {trainer.linkedInUrl && (
                <a href={trainer.linkedInUrl} target="_blank" rel="noreferrer" className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 text-xs text-white rounded-lg flex items-center gap-1 font-bold">
                  <Linkedin size={11} /> LinkedIn
                </a>
              )}
              {trainer.portfolioUrl && (
                <a href={trainer.portfolioUrl} target="_blank" rel="noreferrer" className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 text-xs text-white rounded-lg flex items-center gap-1 font-bold">
                  <Globe size={11} /> Portfolio
                </a>
              )}
            </div>
          </div>

          {/* Section 3: Teaching Mandate */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] border-b border-zinc-800 pb-2 flex items-center gap-2">
              <BookOpen size={14} /> Cohort Teaching Mandate
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Assigned Cohort Track:</span>
                <span className="text-zinc-200 font-bold">{trainer.preferredCohortTrack || 'Class of 2026 Alpha'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Teaching Modality:</span>
                <span className="text-zinc-200">{trainer.teachingModality || 'Online Synchronous'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Available Hours / Week:</span>
                <span className="text-zinc-200 font-mono font-bold">{trainer.weeklyAvailableHours || 10} Hours</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">DIS Affiliation:</span>
                <span className="text-zinc-200">{trainer.alumniStatus || 'DIS Alumnus'}</span>
              </div>
            </div>
          </div>

          {/* Section 4: Remuneration & Payout */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] border-b border-zinc-800 pb-2 flex items-center gap-2">
              <DollarSign size={14} /> Faculty Remuneration &amp; Banking
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Payout Method:</span>
                <span className="text-zinc-200 font-bold">{trainer.payoutMethod || 'M-Pesa'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Payout M-Pesa Number:</span>
                <span className="text-zinc-200 font-mono">{trainer.payoutMpesa || trainer.phone || '-'}</span>
              </div>
              {trainer.payoutBankName && (
                <>
                  <div>
                    <span className="text-zinc-500 font-bold block">Bank Name:</span>
                    <span className="text-zinc-200">{trainer.payoutBankName} ({trainer.payoutBankBranch || 'Main Branch'})</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 font-bold block">Bank Account Number:</span>
                    <span className="text-zinc-200 font-mono">{trainer.payoutAccountNumber}</span>
                  </div>
                </>
              )}
              <div>
                <span className="text-zinc-500 font-bold block">KRA PIN Number:</span>
                <span className="text-zinc-200 font-mono">{trainer.kraPinNumber || trainer.kraPin || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Vetting M-Pesa Ref (KES 2,000):</span>
                <span className="text-amber-400 font-mono font-bold">{trainer.mpesaTransactionCode || trainer.mpesaCode || '-'}</span>
              </div>
            </div>
          </div>

          {/* Section 5: Documents */}
          {(trainer.nationalIdUrl || trainer.academicCertUrl || trainer.cvUrl) && (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
              <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] border-b border-zinc-800 pb-2 flex items-center gap-2">
                <FileText size={14} /> KYC &amp; Verification Documents
              </h3>
              <div className="flex flex-wrap gap-2 text-xs">
                {trainer.nationalIdUrl && (
                  <a href={trainer.nationalIdUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-1 font-bold">
                    <ExternalLink size={11} /> National ID
                  </a>
                )}
                {trainer.academicCertUrl && (
                  <a href={trainer.academicCertUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-1 font-bold">
                    <ExternalLink size={11} /> Degree Certificate
                  </a>
                )}
                {trainer.cvUrl && (
                  <a href={trainer.cvUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg flex items-center gap-1 font-bold">
                    <ExternalLink size={11} /> CV / Resume
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Admin Status Switcher */}
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-zinc-400">Faculty Status</span>
            <div className="flex gap-2">
              <button 
                onClick={() => onUpdateStatus(trainer.id, 'active')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${trainer.status === 'active' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Active
              </button>
              <button 
                onClick={() => onUpdateStatus(trainer.id, 'pending_verification')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${trainer.status === 'pending_verification' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Pending
              </button>
              <button 
                onClick={() => onUpdateStatus(trainer.id, 'suspended')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${trainer.status === 'suspended' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Suspended
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const TrainerManager = ({ showDemo, setShowDemo }) => {
    const navigate = useNavigate();
    const [trainers, setTrainers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [filterAccess, setFilterAccess] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedTrainer, setSelectedTrainer] = useState(null);
    const [pinnedColumn, setPinnedColumn] = useState('name');

    // Comprehensive Registration Form State (Matching 100% of onboarding-mentor.html)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [nationalId, setNationalId] = useState('');
    const [gender, setGender] = useState('Male');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [country, setCountry] = useState('Kenya');
    const [county, setCounty] = useState('Nairobi');
    const [postalAddress, setPostalAddress] = useState('');

    const [highestEducationLevel, setHighestEducationLevel] = useState("Bachelor's Degree");
    const [universityGraduated, setUniversityGraduated] = useState('University of Nairobi');
    const [graduationYear, setGraduationYear] = useState('2018');
    const [degreeCertUrl, setDegreeCertUrl] = useState('');
    const [currentEmployer, setCurrentEmployer] = useState('');
    const [currentJobTitle, setCurrentJobTitle] = useState('Senior Software Engineer');
    const [yearsOfExperience, setYearsOfExperience] = useState(5);
    const [cvUrl, setCvUrl] = useState('');
    const [githubUrl, setGithubUrl] = useState('');
    const [linkedInUrl, setLinkedInUrl] = useState('');
    const [portfolioUrl, setPortfolioUrl] = useState('');

    const [specialization, setSpecialization] = useState('Software Engineering & AI');
    const [secondaryCompetencies, setSecondaryCompetencies] = useState('Python, React, AWS, Docker');
    const [preferredCohortTrack, setPreferredCohortTrack] = useState('Class of 2026 Alpha');
    const [weeklyAvailableHours, setWeeklyAvailableHours] = useState(10);
    const [teachingModality, setTeachingModality] = useState('Online Synchronous');
    const [nexusAccess, setNexusAccess] = useState('Faculty Access');
    const [alumniStatus, setAlumniStatus] = useState('DIS Alumnus');
    const [bio, setBio] = useState('');

    const [payoutMethod, setPayoutMethod] = useState('M-Pesa');
    const [payoutMpesa, setPayoutMpesa] = useState('');
    const [payoutBankName, setPayoutBankName] = useState('');
    const [payoutAccountNumber, setPayoutAccountNumber] = useState('');
    const [payoutBankBranch, setPayoutBankBranch] = useState('');
    const [kraPinNumber, setKraPinNumber] = useState('');
    const [mpesaTransactionCode, setMpesaTransactionCode] = useState('');

    const fetchTrainers = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'trainers'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));

            if (showDemo) {
                data.unshift(
                  {
                    id: 'trainer-kaloki-01',
                    name: 'Charles Kaloki',
                    firstName: 'Charles',
                    lastName: 'Kaloki',
                    email: 'c.kaloki@baringo.go.ke',
                    phone: '+254 712 345 678',
                    nationalId: '28991122',
                    gender: 'Male',
                    country: 'Kenya',
                    county: 'Baringo',
                    highestEducationLevel: "Master's in Distributed Systems",
                    universityGraduated: 'SUNY Potsdam & JKUAT',
                    graduationYear: '2016',
                    currentEmployer: 'County Government & DIS Ecosystem',
                    currentJobTitle: 'Lead Field Operations Engineer',
                    yearsOfExperience: 9,
                    specialization: 'Software Engineering & AI',
                    primarySpecialization: 'Software Engineering & AI',
                    secondaryCompetencies: 'Distributed Systems, COTS Architecture, React, Node.js',
                    preferredCohortTrack: 'Class of 2026 Alpha',
                    weeklyAvailableHours: 15,
                    teachingModality: 'Hybrid Intensive',
                    nexusAccess: 'Nexus Research Core',
                    alumniStatus: 'DIS Alumnus',
                    payoutMethod: 'M-Pesa',
                    payoutMpesa: '+254 712 345 678',
                    kraPinNumber: 'A019922881X',
                    mpesaTransactionCode: 'MPESA-VET-9921',
                    status: 'active',
                    addedAt: '2026-06-15T08:00:00Z'
                  },
                  {
                    id: 'trainer-chebet-02',
                    name: 'Eng. Chebet Kiplagat',
                    firstName: 'Chebet',
                    lastName: 'Kiplagat',
                    email: 'c.kiplagat@tech.ac.ke',
                    phone: '+254 722 888 111',
                    nationalId: '31002233',
                    gender: 'Female',
                    country: 'Kenya',
                    county: 'Nairobi',
                    highestEducationLevel: "Master's Degree",
                    universityGraduated: 'University of Nairobi',
                    graduationYear: '2019',
                    currentEmployer: 'Safaricom PLC',
                    currentJobTitle: 'Senior Cloud Security Architect',
                    yearsOfExperience: 7,
                    specialization: 'Cloud & Cyber Architecture',
                    primarySpecialization: 'Cloud & Cyber Architecture',
                    secondaryCompetencies: 'AWS, Kubernetes, Zero Trust, Terraform',
                    preferredCohortTrack: 'Accelerated Winter 2026',
                    weeklyAvailableHours: 10,
                    teachingModality: 'Online Synchronous',
                    nexusAccess: 'Faculty Access',
                    alumniStatus: 'External Industry Expert',
                    payoutMethod: 'Bank Wire',
                    payoutBankName: 'KCB Bank Kenya',
                    payoutAccountNumber: '1288339911',
                    payoutBankBranch: 'Kipande House',
                    kraPinNumber: 'A008811229K',
                    mpesaTransactionCode: 'MPESA-VET-8812',
                    status: 'active',
                    addedAt: '2026-07-01T12:00:00Z'
                  }
                );
            }
            setTrainers(data);
        } catch (e) {
            console.error("Error fetching trainers:", e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchTrainers();
    }, [showDemo]);

    const handleAdd = async (e) => {
        e.preventDefault();
        const fullName = `${firstName} ${lastName}`.trim() || email.split('@')[0];
        if (!email.trim() || !email.includes('@')) return;
        setStatusMessage('saving');

        const cleanEmail = email.trim().toLowerCase();
        const trainerId = cleanEmail.replace(/[^a-z0-9]/g, '-');

        try {
            await setDoc(doc(db, 'trainers', trainerId), {
                name: fullName,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: cleanEmail,
                phone: phone.trim(),
                nationalId: nationalId.trim(),
                gender,
                dateOfBirth,
                country: country.trim(),
                county: county.trim(),
                postalAddress: postalAddress.trim(),
                highestEducationLevel,
                universityGraduated: universityGraduated.trim(),
                graduationYear: graduationYear.trim(),
                degreeCertUrl: degreeCertUrl.trim(),
                currentEmployer: currentEmployer.trim(),
                currentJobTitle: currentJobTitle.trim(),
                yearsOfExperience: parseInt(yearsOfExperience) || 0,
                cvUrl: cvUrl.trim(),
                githubUrl: githubUrl.trim(),
                linkedInUrl: linkedInUrl.trim(),
                portfolioUrl: portfolioUrl.trim(),
                specialization,
                primarySpecialization: specialization,
                secondaryCompetencies: secondaryCompetencies.trim(),
                preferredCohortTrack,
                weeklyAvailableHours: parseInt(weeklyAvailableHours) || 0,
                teachingModality,
                nexusAccess,
                alumniStatus,
                bio: bio.trim(),
                payoutMethod,
                payoutMpesa: payoutMpesa.trim() || phone.trim(),
                payoutBankName: payoutBankName.trim(),
                payoutAccountNumber: payoutAccountNumber.trim(),
                payoutBankBranch: payoutBankBranch.trim(),
                kraPinNumber: kraPinNumber.trim(),
                mpesaTransactionCode: mpesaTransactionCode.trim(),
                status: 'active',
                updatedAt: new Date().toISOString(),
                createdAt: new Date().toISOString()
            }, { merge: true });

            setFirstName('');
            setLastName('');
            setEmail('');
            setPhone('');
            setNationalId('');
            setCurrentEmployer('');
            setGithubUrl('');
            setLinkedInUrl('');
            setMpesaTransactionCode('');
            setIsFormOpen(false);

            setStatusMessage('success');
            fetchTrainers();
            setTimeout(() => setStatusMessage(''), 3000);
        } catch (e) {
            console.error("Error adding faculty:", e);
            setStatusMessage('error');
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
      try {
        await setDoc(doc(db, 'trainers', id), {
          status: newStatus,
          statusUpdatedAt: new Date().toISOString()
        }, { merge: true });
        
        setTrainers(prev => prev.map(t => t.id === id ? { ...t, status: newStatus } : t));
        if (selectedTrainer && selectedTrainer.id === id) {
          setSelectedTrainer(prev => ({ ...prev, status: newStatus }));
        }
      } catch (err) {
        console.error("Failed to update trainer status:", err);
      }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Permanently remove this trainer?")) return;
        try {
            await deleteDoc(doc(db, 'trainers', id));
            if (selectedTrainer?.id === id) setSelectedTrainer(null);
            fetchTrainers();
        } catch (e) {
            console.error(e);
        }
    };

    const filteredTrainers = trainers.filter(t => {
      const matchesAccess = filterAccess === 'all' || (t.nexusAccess || '').toLowerCase() === filterAccess.toLowerCase();
      const query = searchTerm.toLowerCase();
      const tName = (t.name || `${t.firstName || ''} ${t.lastName || ''}`).toLowerCase();
      const tEmail = (t.email || '').toLowerCase();
      const tSpec = (t.primarySpecialization || t.specialization || '').toLowerCase();
      const tEmployer = (t.currentEmployer || '').toLowerCase();
      const matchesSearch = !searchTerm || tName.includes(query) || tEmail.includes(query) || tSpec.includes(query) || tEmployer.includes(query);
      return matchesAccess && matchesSearch;
    });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedTrainers = [...filteredTrainers].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = (a[sortConfig.key] || '').toString().toLowerCase();
        let bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B1A2D]/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="p-2 bg-[#8B1A2D]/20 text-[#BE123C] border border-[#8B1A2D]/30 rounded-xl">
                                <ShieldCheck size={18} />
                            </span>
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                                Portal 02 &bull; Faculty &amp; Mentor Network
                            </span>
                        </div>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight">Faculty &amp; Mentor Registry</h1>
                        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-2 max-w-xl">
                            Senior software engineers, AI architects, faculty fellows, and curriculum committee leaders.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsFormOpen(!isFormOpen)}
                            className="bg-[#8B1A2D] hover:bg-[#6c1423] text-white font-black uppercase tracking-widest text-xs px-6 py-3.5 rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
                        >
                            <Plus size={16} /> {isFormOpen ? 'Close Form' : 'Register Faculty Fellow'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Comprehensive Registration Form (Matching onboarding-mentor.html 100%) */}
            {isFormOpen && (
              <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-3xl shadow-2xl space-y-6">
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                      <div>
                          <h2 className="text-base font-black uppercase tracking-widest text-[#BE123C] flex items-center gap-2">
                              <GraduationCap size={18} /> Register Faculty Member &bull; 100% Onboarding Schema
                          </h2>
                          <p className="text-xs text-zinc-400 mt-1">
                              Record academic credentials, employer track record, GitHub portfolio, teaching hours, and payout M-Pesa.
                          </p>
                      </div>
                      <button 
                          onClick={() => setIsFormOpen(false)}
                          className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
                      >
                          <X size={18} />
                      </button>
                  </div>

                  <form onSubmit={handleAdd} className="space-y-6">
                      {/* Section 1: Personal & KYC */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] flex items-center gap-2">
                              <User size={14} /> 1. Personal &amp; Contact Credentials
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">First Name *</label>
                                  <input 
                                      type="text" 
                                      value={firstName} 
                                      onChange={e => setFirstName(e.target.value)} 
                                      placeholder="e.g. Charles" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Last Name *</label>
                                  <input 
                                      type="text" 
                                      value={lastName} 
                                      onChange={e => setLastName(e.target.value)} 
                                      placeholder="e.g. Kaloki" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Email *</label>
                                  <input 
                                      type="email" 
                                      value={email} 
                                      onChange={e => setEmail(e.target.value)} 
                                      placeholder="c.kaloki@domain.com" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">WhatsApp Phone *</label>
                                  <input 
                                      type="tel" 
                                      value={phone} 
                                      onChange={e => setPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">National ID / Passport No *</label>
                                  <input 
                                      type="text" 
                                      value={nationalId} 
                                      onChange={e => setNationalId(e.target.value)} 
                                      placeholder="e.g. 28991122" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Gender</label>
                                  <select 
                                      value={gender} 
                                      onChange={e => setGender(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D] font-bold"
                                  >
                                      <option>Male</option>
                                      <option>Female</option>
                                      <option>Prefer not to say</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Operating County</label>
                                  <input 
                                      type="text" 
                                      value={county} 
                                      onChange={e => setCounty(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Country</label>
                                  <input 
                                      type="text" 
                                      value={country} 
                                      onChange={e => setCountry(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 2: Academic & Industry Experience */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] flex items-center gap-2">
                              <GraduationCap size={14} /> 2. Academic Background &amp; Industry Track Record
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Highest Degree Attained</label>
                                  <select 
                                      value={highestEducationLevel} 
                                      onChange={e => setHighestEducationLevel(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D] font-bold"
                                  >
                                      <option>Doctorate / PhD</option>
                                      <option>Master's Degree</option>
                                      <option>Bachelor's Degree</option>
                                      <option>Higher National Diploma</option>
                                      <option>Professional Industry Expert</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">University Graduated</label>
                                  <input 
                                      type="text" 
                                      value={universityGraduated} 
                                      onChange={e => setUniversityGraduated(e.target.value)} 
                                      placeholder="e.g. University of Nairobi" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Graduation Year</label>
                                  <input 
                                      type="text" 
                                      value={graduationYear} 
                                      onChange={e => setGraduationYear(e.target.value)} 
                                      placeholder="e.g. 2018" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Years Industry Experience</label>
                                  <input 
                                      type="number" 
                                      value={yearsOfExperience} 
                                      onChange={e => setYearsOfExperience(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Current Employer</label>
                                  <input 
                                      type="text" 
                                      value={currentEmployer} 
                                      onChange={e => setCurrentEmployer(e.target.value)} 
                                      placeholder="e.g. Safaricom / Google Africa" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Current Job Title</label>
                                  <input 
                                      type="text" 
                                      value={currentJobTitle} 
                                      onChange={e => setCurrentJobTitle(e.target.value)} 
                                      placeholder="e.g. Senior Staff Engineer" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">GitHub Profile URL</label>
                                  <input 
                                      type="url" 
                                      value={githubUrl} 
                                      onChange={e => setGithubUrl(e.target.value)} 
                                      placeholder="https://github.com/..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">LinkedIn Profile URL</label>
                                  <input 
                                      type="url" 
                                      value={linkedInUrl} 
                                      onChange={e => setLinkedInUrl(e.target.value)} 
                                      placeholder="https://linkedin.com/in/..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 3: Specialization & Teaching Mandate */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] flex items-center gap-2">
                              <BookOpen size={14} /> 3. Faculty Specialization &amp; Cohort Mandate
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Primary Specialization *</label>
                                  <select 
                                      value={specialization} 
                                      onChange={e => setSpecialization(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D] font-bold"
                                  >
                                      <option>Software Engineering & AI</option>
                                      <option>Cloud & Cyber Architecture</option>
                                      <option>Data Science & Analytics</option>
                                      <option>Distributed Systems & Blockchain</option>
                                      <option>Embedded IoT Systems</option>
                                      <option>Digital Media & Cinema Tech</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Teaching Modality</label>
                                  <select 
                                      value={teachingModality} 
                                      onChange={e => setTeachingModality(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D] font-bold"
                                  >
                                      <option>Online Synchronous</option>
                                      <option>Hybrid Intensive</option>
                                      <option>Onsite Campus Lab</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Weekly Teaching Hours</label>
                                  <input 
                                      type="number" 
                                      value={weeklyAvailableHours} 
                                      onChange={e => setWeeklyAvailableHours(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Nexus Research Tier</label>
                                  <select 
                                      value={nexusAccess} 
                                      onChange={e => setNexusAccess(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D] font-bold"
                                  >
                                      <option>Nexus Research Core</option>
                                      <option>Faculty Access</option>
                                      <option>Observer / Guest</option>
                                  </select>
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Secondary Technical Competencies</label>
                                  <input 
                                      type="text" 
                                      value={secondaryCompetencies} 
                                      onChange={e => setSecondaryCompetencies(e.target.value)} 
                                      placeholder="e.g. Python, Docker, React, PyTorch, Golang" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Assigned Cohort Track</label>
                                  <input 
                                      type="text" 
                                      value={preferredCohortTrack} 
                                      onChange={e => setPreferredCohortTrack(e.target.value)} 
                                      placeholder="e.g. Class of 2026 Alpha" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 4: Remuneration & Payout */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-[#BE123C] flex items-center gap-2">
                              <DollarSign size={14} /> 4. Faculty Remuneration &amp; Banking Details
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Payout Method</label>
                                  <select 
                                      value={payoutMethod} 
                                      onChange={e => setPayoutMethod(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D] font-bold"
                                  >
                                      <option>M-Pesa</option>
                                      <option>Bank Wire</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Payout M-Pesa Phone</label>
                                  <input 
                                      type="tel" 
                                      value={payoutMpesa} 
                                      onChange={e => setPayoutMpesa(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Bank Name</label>
                                  <input 
                                      type="text" 
                                      value={payoutBankName} 
                                      onChange={e => setPayoutBankName(e.target.value)} 
                                      placeholder="e.g. KCB / Equity Bank" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Account Number</label>
                                  <input 
                                      type="text" 
                                      value={payoutAccountNumber} 
                                      onChange={e => setPayoutAccountNumber(e.target.value)} 
                                      placeholder="e.g. 1288339911" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">KRA PIN Number</label>
                                  <input 
                                      type="text" 
                                      value={kraPinNumber} 
                                      onChange={e => setKraPinNumber(e.target.value)} 
                                      placeholder="e.g. A019922881X" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Vetting M-Pesa Transaction Ref (KES 2,000)</label>
                                  <input 
                                      type="text" 
                                      value={mpesaTransactionCode} 
                                      onChange={e => setMpesaTransactionCode(e.target.value)} 
                                      placeholder="e.g. QWE8910XYZ" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-amber-400 font-mono font-bold text-xs focus:outline-none focus:border-[#8B1A2D]"
                                  />
                              </div>
                          </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                          <button 
                              type="button" 
                              onClick={() => setIsFormOpen(false)}
                              className="px-6 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white text-xs font-black uppercase tracking-widest cursor-pointer"
                          >
                              Cancel
                          </button>
                          <button 
                              type="submit" 
                              disabled={statusMessage === 'saving'}
                              className="bg-[#8B1A2D] hover:bg-[#6c1423] text-white font-black uppercase tracking-widest text-xs px-8 py-3 rounded-xl transition-all shadow-lg cursor-pointer"
                          >
                              {statusMessage === 'saving' ? 'Saving Profile...' : 'Register Faculty Fellow'}
                          </button>
                      </div>
                  </form>
                  {statusMessage === 'success' && (
                      <p className="text-emerald-400 text-xs font-bold flex items-center gap-1.5 justify-end">
                          <CheckCircle size={14} /> Faculty Fellow credentials synchronized successfully.
                      </p>
                  )}
              </div>
            )}

            {/* Google Sheets-Grade Modular Registry Sheet with Freeze Panes */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                {/* Spreadsheet Toolbar */}
                <div className="p-4 border-b border-zinc-800 flex flex-wrap gap-4 justify-between items-center bg-zinc-900/60">
                    <div className="flex flex-1 items-center gap-3 min-w-[280px]">
                      <Search size={16} className="text-zinc-400" />
                      <input 
                        type="text"
                        placeholder="Search by faculty name, specialization, employer, or email..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Filter size={14} className="text-zinc-400" />
                        <select
                            value={filterAccess}
                            onChange={e => setFilterAccess(e.target.value)}
                            className="bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-widest px-3 py-1.5 rounded-lg focus:outline-none"
                        >
                            <option value="all">All Tiers</option>
                            <option value="nexus research core">Nexus Core</option>
                            <option value="faculty access">Faculty Access</option>
                            <option value="observer / guest">Observer / Guest</option>
                        </select>
                      </div>

                      <button 
                          onClick={fetchTrainers}
                          className="text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full border border-zinc-700 text-zinc-400 bg-zinc-900 hover:text-white transition-all shadow-sm cursor-pointer"
                      >
                          Refresh
                      </button>
                    </div>
                </div>

                {/* Horizontal Scrolling High-Density Table with Freeze Panes */}
                <div className="overflow-x-auto relative">
                  <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                      <thead>
                          <tr className="bg-zinc-900/90 border-b border-zinc-800 select-none">
                              {/* Freeze Pane: Index */}
                              <th className={`w-12 p-3 text-center text-[10px] font-black uppercase tracking-wider text-zinc-500 border-r border-zinc-800 ${
                                pinnedColumn !== 'none' ? 'sticky left-0 z-30 bg-zinc-900' : ''
                              }`}>
                                #
                              </th>

                              {/* Freeze Pane: Faculty Name */}
                              <th className={`min-w-[220px] p-0 border-r border-zinc-800 ${
                                pinnedColumn === 'name' 
                                  ? 'sticky left-12 z-30 bg-zinc-900 border-r-2 border-r-[#BE123C] shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                  : ''
                              }`}>
                                <div className="w-full h-full p-3 flex items-center justify-between">
                                  <button onClick={() => handleSort('name')} className="flex items-center gap-1 font-black uppercase tracking-widest text-zinc-300 hover:text-white">
                                    <span>Faculty Name</span> {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                                  </button>
                                  <button
                                    onClick={() => setPinnedColumn(pinnedColumn === 'name' ? 'none' : 'name')}
                                    className={`p-1 rounded cursor-pointer ${pinnedColumn === 'name' ? 'text-[#BE123C]' : 'text-zinc-600 hover:text-zinc-300'}`}
                                    title={pinnedColumn === 'name' ? 'Unpin column' : 'Pin Faculty Name'}
                                  >
                                    <Pin size={11} className={pinnedColumn === 'name' ? 'fill-[#BE123C]' : ''} />
                                  </button>
                                </div>
                              </th>

                              {/* Specialization */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Primary Specialization
                              </th>

                              {/* Current Employer */}
                              <th className="min-w-[170px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Current Employer
                              </th>

                              {/* Highest Degree */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Degree &amp; Alma Mater
                              </th>

                              {/* Years Exp */}
                              <th className="min-w-[100px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Experience
                              </th>

                              {/* Contact Email */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Email Address
                              </th>

                              {/* WhatsApp Phone */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Phone Number
                              </th>

                              {/* National ID */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                National ID
                              </th>

                              {/* County / Country */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Location
                              </th>

                              {/* Teaching Modality */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Modality
                              </th>

                              {/* Available Hours */}
                              <th className="min-w-[110px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Weekly Hrs
                              </th>

                              {/* Nexus Access */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Nexus Tier
                              </th>

                              {/* Vetting Code */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Vetting Ref
                              </th>

                              {/* Status */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Status
                              </th>

                              {/* Actions */}
                              <th className="min-w-[110px] p-3 text-center font-black uppercase tracking-widest text-zinc-400">
                                Action
                              </th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/60 font-medium">
                          {loading ? (
                              <tr><td colSpan="16" className="p-8 text-center text-zinc-500 text-xs">Loading Faculty Registry...</td></tr>
                          ) : sortedTrainers.length === 0 ? (
                              <tr><td colSpan="16" className="p-8 text-center text-zinc-500 text-xs">No faculty members found.</td></tr>
                          ) : sortedTrainers.map((trainer, idx) => {
                              const displayName = trainer.name || `${trainer.firstName || ''} ${trainer.lastName || ''}`.trim() || 'Trainer';

                              return (
                                <tr 
                                    key={trainer.id} 
                                    onClick={() => setSelectedTrainer(trainer)}
                                    className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                                >
                                    {/* Pinned: Index */}
                                    <td className={`p-3 text-center font-mono text-[11px] text-zinc-500 border-r border-zinc-800 ${
                                      pinnedColumn !== 'none' ? 'sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900' : ''
                                    }`}>
                                        {idx + 1}
                                    </td>

                                    {/* Pinned: Name */}
                                    <td className={`p-3 border-r border-zinc-800 ${
                                      pinnedColumn === 'name' 
                                        ? 'sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r-2 border-r-[#BE123C] shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                        : ''
                                    }`}>
                                        <p className="font-bold text-white group-hover:text-[#BE123C] transition-colors">{displayName}</p>
                                        <p className="text-[10px] text-zinc-500">{trainer.currentJobTitle || 'Faculty Fellow'}</p>
                                    </td>

                                    {/* Specialization */}
                                    <td className="p-3 border-r border-zinc-800 font-semibold text-zinc-200">
                                        {trainer.primarySpecialization || trainer.specialization || 'Software Engineering'}
                                    </td>

                                    {/* Employer */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {trainer.currentEmployer || 'Independent Expert'}
                                    </td>

                                    {/* Degree */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-400">
                                        <span>{trainer.highestEducationLevel || "Bachelor's Degree"}</span>
                                        <span className="block text-[10px] text-zinc-500">{trainer.universityGraduated || '-'}</span>
                                    </td>

                                    {/* Years Exp */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-zinc-300 font-bold">
                                        {trainer.yearsOfExperience || 0} Yrs
                                    </td>

                                    {/* Email */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-zinc-300">
                                        {trainer.email || '-'}
                                    </td>

                                    {/* Phone */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-zinc-400">
                                        {trainer.phone || '-'}
                                    </td>

                                    {/* National ID */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-zinc-400">
                                        {trainer.nationalId || '-'}
                                    </td>

                                    {/* Location */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {trainer.county || '-'}, {trainer.country || 'Kenya'}
                                    </td>

                                    {/* Modality */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {trainer.teachingModality || 'Online'}
                                    </td>

                                    {/* Hours */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-amber-400 font-bold">
                                        {trainer.weeklyAvailableHours || 0} hrs/wk
                                    </td>

                                    {/* Nexus Tier */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className="text-[10px] px-2 py-0.5 rounded font-black uppercase bg-[#8B1A2D]/20 text-[#BE123C] border border-[#8B1A2D]/40">
                                            {trainer.nexusAccess || 'Faculty'}
                                        </span>
                                    </td>

                                    {/* Vetting Ref */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-amber-400">
                                        {trainer.mpesaTransactionCode || trainer.mpesaCode || '-'}
                                    </td>

                                    {/* Status */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border ${
                                          trainer.status === 'active' 
                                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        }`}>
                                          {trainer.status || 'Active'}
                                        </span>
                                    </td>

                                    {/* Action */}
                                    <td className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                            <button 
                                                onClick={() => setSelectedTrainer(trainer)}
                                                className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                                title="Inspect Faculty Dossier"
                                            >
                                                <Eye size={12} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(trainer.id)}
                                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                                                title="Delete Faculty"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                              );
                          })}
                      </tbody>
                  </table>
                </div>
            </div>

            {/* Slide-over Full Dossier Drawer */}
            <TrainerDetailDrawer 
              trainer={selectedTrainer} 
              onClose={() => setSelectedTrainer(null)} 
              onUpdateStatus={handleUpdateStatus}
            />
        </div>
    );
};

export default TrainerManager;
